<template>
  <div class="d-flex flex-column justify-space-between fill-height">
    <div class="flex-shrink-0">
      <!-- display: block -->
      <v-text-field
        v-model="searchText"
        solo
        clearable
        label="Search Classes"
        messages="Start typing"
        prepend-inner-icon="mdi-magnify"
        @input="onInput"
        ref="searchField"
      ></v-text-field>
    </div>

    <!-- display: block -->
    <div class="flex-grow-1 scrollable-treeview">
      <v-treeview
        activatable
        @update:active="onActive"
        :search="debouncedSearchText"
        :items="tree"
        :open.sync="open"
      ></v-treeview>
    </div>

    <!-- display: block -->
    <div class="flex-shrink-0">
      <!-- display: block -->
      <v-alert v-model="error" type="error" dismissible class="mt-4">
        Error creating new individual
      </v-alert>
      <v-btn
        v-if="!error"
        text
        color="secondary"
        :disabled="!activatedType || persisting"
        :loading="persisting"
        @click="onCreate"
        class="mt-4"
      >
        {{ activatedText }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { Iri } from '@/rec-cache/rec-cache-types'
import debounce from 'debounce'

export default {
  props: {
    // Needed when creating a relationship, ie connected neighbor.
    focusId: {
      type: Object,
      default: () => new Iri(),
    },
    // Prop is an array of [source, relationship, target] all Iris, where source and relationship are types and target the individual id.
    inNeighbors: {
      type: Array,
      default: () => [],
    },
    // Prop is an array of [source, relationship, target] all Iris, where source is the individual id and relationship and target are types.
    outNeighbors: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    tree: [],
    open: [],
    activated: '',
    inNeighbor: null,
    outNeighbor: null,
    searchText: '',
    debouncedSearchText: '',
    persisting: false,
    error: false,
  }),
  computed: {
    activatedType: function () {
      return this.activated?.type?.short || ''
    },
    activatedRelationship: function () {
      return (this.inNeighbor || this.outNeighbor)?.[1]?.short || ''
    },
    activatedText: function () {
      return (
        'Create ' +
        this.activatedType +
        (this.activatedRelationship ? ` [${this.activatedRelationship}]` : '')
      )
    },
  },
  mounted() {
    // TODO figure how to open all when re-entering... perhaps open all on exit
    // TODO if the in and out neighbors are set and the id isnt an error will occur at on create... catch in general cannot compelte operation fault handling/message.
    console.log(
      'components.create-individual.mounted',
      this.neighbor,
      this.inNeighbors,
      this.outNeighbors
    )
    this.classTree()

    // Create tree view, may eventually become the role of the wob object.
    // Apply any policies/rules to remove (disable, dim out or remove, make selectable)

    // Open all tree nodes.
    this.open = (function expandable(nodes, iteration = 1) {
      const parents = nodes.filter((node) => node.children)
      // console.log('EXPANDABLE', iteration, nodes, parents)
      return parents
        .map((parent) => expandable(parent.children, ++iteration))
        .reduce((prev, curr) => prev.concat(curr), [])
        .concat(parents.map((parent) => parent.id))
    })(this.tree)
  },
  created() {
    // console.log('components.create-individual.created', this.neighborType)
    // console.log('REFS', this.$refs)
    // TODO consider if the search field shall be focused... if so this doesnt work as it only focuses first time
    // this.$nextTick(function () {
    //   this.$refs.searchField.focus()
    // })
    // TODO this is a superclasses test...
    /*
    const sc = this.$wobo.superclasses(new Iri('https://w3id.org/rec#Library'))
    console.log('SUPERCLASSES', sc)
    console.log('SC CACHE', this.$wobo._scCache)
    const neighbors = this.$wobo.neighbors(
      new Iri('https://w3id.org/rec#Library')
    )
    console.log('NEIGHBORS', neighbors)
    const subc = this.$wobo.subclasses(new Iri('https://w3id.org/rec#Space'))
    console.log('SUBCLASSES', subc)
    console.log('ALL CLASSES', this.$wobo.classes.length)
    */
  },
  methods: {
    classTree() {
      // The types that may be created
      const neighborTypes = [
        ...this.inNeighbors.map(([source]) => source),
        ...this.outNeighbors.map(([, , target]) => target),
      ]

      console.log(
        '>>>>> CLASS TREE',
        'components.create-individual.methods.class-tree.neighbor-types',
        neighborTypes
      )

      // Sort alphabetically.
      const classes = this.$wobo.classes.sort((a, b) =>
        a.type.short.localeCompare(b.type.short)
      )

      // Use the in or out neighbor types if set.
      // TODO unclear why the falsy branch both check is root and the other condition...
      const parents = neighborTypes.length
        ? classes.filter((c) => neighborTypes.find((nt) => c.hasType(nt)))
        : classes.filter(
            (c) =>
              c.isRoot ||
              !c.superclasses.find((sc) =>
                classes.find((_c) => _c.type.equals(sc))
              )
          )
      console.log(
        'components.create-individual.methods.class-tree.parents',
        classes.length,
        parents
      )

      // Recursive utility to traverse class hierarchy downwards.
      // Parameter parent is an type Iri.
      // Returns tree of tree nodes.
      // TODO consider making IIFE...
      function descendants(parent, iteration = 1) {
        const parentNode = { id: parent.value, name: parent.short }
        const children = classes.filter((c) => c.hasParent(parent))
        // console.log('DESCENDANTS', iteration, parentNode, children)
        if (children.length)
          parentNode.children = children.map((child) =>
            descendants(child.type, ++iteration)
          )
        return parentNode
      }
      this.tree = parents.map((parent) => descendants(parent.type))
    },

    onActive(active) {
      // Invoked with array of active tree node ids. Or an empty array at deselection.
      console.log('components.create-individual.methods.on-active', active)
      const [activated] = active
      this.activated =
        activated && this.$wobo.classes.find((c) => c.type.equals(activated))
      console.log(
        'components.create-individual.methods.on-active.activated',
        this.activated
      )

      // Decide if an in or outgoing neighbor is selected.
      if (this.activated) {
        // Check if any of the in or out neighbors are in the list of superclasses, or the activated type itself.
        const superclasses = this.$wobo
          .superclasses(this.activated.type)
          .concat(this.activated.type)
        console.log('>>> WHEN ACTIVATED', this.activated, superclasses)

        this.inNeighbor = this.inNeighbors.find(([source]) =>
          superclasses.find((sc) => sc.equals(source))
        )

        this.outNeighbor = this.outNeighbors.find(([, , target]) =>
          superclasses.find((sc) => sc.equals(target))
        )

        console.log('IN NEIGHBOR', this.inNeighbor)
        console.log('OUT NEIGHBOR', this.outNeighbor)
      }
    },
    // Debounce used as the treeview search is fairly slow.
    // TODO consider implementing a faster filter...
    onInput: debounce(function () {
      this.debouncedSearchText = this.searchText
    }, 300),
    async onCreate() {
      console.log('components.create-individual.methods.on-create')
      try {
        this.persisting = true
        const newId = await this.$wob.createAsync(this.activated?.type)
        console.log('components.create-individual.methods.on-create.id', newId)
        // TODO create the relationship...
        // Decide if its an incoming or outgoing relationship.
        if (this.inNeighbor) {
          const newIndividual = this.$wob.read(newId)
          const [, relationship] = this.inNeighbor
          newIndividual.addProperties(
            this.$wob.owl.objectProperty(relationship, [this.focusId])
          )
          console.log('ON CREATE IN NEGHBOR', newIndividual)
          await this.$wob.updateAsync(newIndividual)
        }
        if (this.outNeighbor) {
          const focusIndividual = this.$wob.read(this.focusId)
          const [, relationship] = this.outNeighbor
          focusIndividual.addProperties(
            this.$wob.owl.objectProperty(relationship, [newId])
          )
          console.log('ON CREATE OUT NEGHBOR', focusIndividual)
          await this.$wob.updateAsync(focusIndividual)
        }

        this.$emit('create', newId)
      } catch (error) {
        console.log(
          'components.create-individual.methods.on-create.error',
          error
        )

        this.error = true
      } finally {
        this.persisting = false
      }
    },
  },
}
</script>

<style scoped>
.fill-height {
  height: 100%; /* Ensures the component fills the viewport height */
}

.scrollable-treeview {
  overflow-y: auto; /* Enables scrolling for the treeview */
}
</style>
